import type { JSX } from 'react';
import { useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { AlertTriangle, User } from 'lucide-react';
import { Alert, Avatar, Heading, useToast } from '@admin-platform/ui/components';

import { Button } from '../Button/Button';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card/Card';
import { FormGroup } from '../Form/FormGroup';
import { Input } from '../Inputs/Input';
import { Label } from '../Inputs/Label';
import InnerContainer from '../Layout/InnerContainer';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../Modal/Modal';
import { PageHeader } from '../PageHeader/PageHeader';
import { useAuth } from '../../hooks/useAuth';
import NotificationPreferences from './NotificationPreferences/NotificationPreferences';
import TimezoneSettings from './TimezoneSettings/TimezoneSettings';
import EmailSignatures from './EmailSignatures/EmailSignatures';
import FollowUpSettings from './FollowUpSettings/FollowUpSettings';
import GmailSettings from './GmailSettings/GmailSettings';
import { NotificationCenterPreferences } from '../NotificationCenter/NotificationCenterPreferences';
import NotificationSettings from './NotificationSettings/NotificationSettings';
import styles from './Profile.module.css';

import DELETE_MY_ACCOUNT from './mutations/deleteMyAccount.mutation.graphql';
import type {
  DeleteMyAccountMutation,
  DeleteMyAccountMutationVariables,
} from './mutations/deleteMyAccount.mutation.graphql';
import REQUEST_EMAIL_CHANGE from './mutations/requestEmailChange.mutation.graphql';
import type {
  RequestEmailChangeMutation,
  RequestEmailChangeMutationVariables,
} from './mutations/requestEmailChange.mutation.graphql';
import UPDATE_MY_NAME from './mutations/updateMyName.mutation.graphql';
import type {
  UpdateMyNameMutation,
  UpdateMyNameMutationVariables,
} from './mutations/updateMyName.mutation.graphql';
import MY_ACCOUNT_DELETION_ELIGIBILITY from './queries/accountDeletionEligibility.query.graphql';
import type { MyAccountDeletionEligibilityQuery } from './queries/accountDeletionEligibility.query.graphql';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export default function ProfilePage(): JSX.Element | null {
  const { user, logout, refreshUser } = useAuth();
  const toast = useToast();

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [updateName, { loading: nameSaving }] = useMutation<
    UpdateMyNameMutation,
    UpdateMyNameMutationVariables
  >(UPDATE_MY_NAME);
  const [requestEmailChange, { loading: emailSubmitting }] = useMutation<
    RequestEmailChangeMutation,
    RequestEmailChangeMutationVariables
  >(REQUEST_EMAIL_CHANGE);
  const [deleteAccount, { loading: deleting }] = useMutation<
    DeleteMyAccountMutation,
    DeleteMyAccountMutationVariables
  >(DELETE_MY_ACCOUNT);

  const {
    data: eligibilityData,
    error: eligibilityError,
    refetch: refetchEligibility,
  } = useQuery<MyAccountDeletionEligibilityQuery>(MY_ACCOUNT_DELETION_ELIGIBILITY, {
    fetchPolicy: 'cache-and-network',
  });
  const eligibility = eligibilityData?.myAccountDeletionEligibility;

  if (!user) {
    return null;
  }

  const startEditingName = () => {
    setNameDraft(user.name ?? '');
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      toast('Name cannot be empty', 'error');
      return;
    }
    try {
      await updateName({ variables: { name: trimmed } });
      // The name is baked into the signed JWT, so refresh it to reflect the change.
      await refreshUser();
      setIsEditingName(false);
      toast('Name updated', 'success');
    } catch (err) {
      toast(errorMessage(err, 'Could not update your name'), 'error');
    }
  };

  const openEmailModal = () => {
    setNewEmail('');
    setEmailSentTo(null);
    setEmailError(null);
    setIsEmailModalOpen(true);
  };

  const handleRequestEmailChange = async () => {
    setEmailError(null);
    try {
      const { data } = await requestEmailChange({ variables: { newEmail: newEmail.trim() } });
      const result = data?.requestEmailChange;
      if (result?.emailSent) {
        setEmailSentTo(result.pendingEmail);
      } else {
        setEmailError(
          'We could not send the verification email right now. Please try again in a moment.',
        );
      }
    } catch (err) {
      setEmailError(errorMessage(err, 'Could not start the email change'));
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      setIsDeleteModalOpen(false);
      toast('Your account has been deleted', 'success');
      logout({ returnTo: '/login' });
    } catch (err) {
      setIsDeleteModalOpen(false);
      toast(errorMessage(err, 'Could not delete your account'), 'error');
    }
  };

  return (
    <InnerContainer>
      <PageHeader
        title="Account"
        description="Your name, mailbox, timezone, signatures, follow-up default, and notifications on this device."
      />

      <div className={styles.Stack}>
        <Card noHover>
          <CardHeader>
            <div className={styles.Header}>
              <span className={styles.HeaderIcon} aria-hidden="true">
                <User />
              </span>
              <div className={styles.HeaderText}>
                <CardTitle>Profile</CardTitle>
                <CardDescription>Your name and sign-in email.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className={styles.IdentityBody}>
            <Avatar size="xxxlarge" name={user.name ?? null} imageSrc={user.picture} />

            <div className={styles.IdentityFields}>
              <div className={styles.Field}>
                <Label htmlFor="account-name">Name</Label>
                {isEditingName ? (
                  <div className={styles.EditRow}>
                    <Input
                      id="account-name"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      placeholder="Your name"
                      autoFocus
                    />
                    <Button variant="primary" onClick={handleSaveName} disabled={nameSaving}>
                      {nameSaving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsEditingName(false)}
                      disabled={nameSaving}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className={styles.ValueRow}>
                    <span className={styles.Value}>{user.name || '—'}</span>
                    <Button variant="outline" size="sm" onClick={startEditingName}>
                      Edit
                    </Button>
                  </div>
                )}
              </div>

              <div className={styles.Field}>
                <Label htmlFor="account-email">Email</Label>
                <div className={styles.ValueRow}>
                  <span className={styles.Value} id="account-email">
                    {user.email}
                  </span>
                  <Button variant="outline" size="sm" onClick={openEmailModal}>
                    Change email
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <TimezoneSettings />

        {/* Which notifications reach this person, and when their digest lands.
          Directly below the timezone because the digest slot is expressed in it. */}
        <NotificationPreferences />

        <div className={styles.Grid}>
          {/* A mailbox is personal — each operator sends as themselves. Tenant
            Drive folder sync stays under Settings → Integrations. */}
          <GmailSettings />

          {/* Notifications are a personal, per-device setting — they live with the
            user's account rather than the tenant-scoped Settings tabs. */}
          <NotificationSettings />
          <NotificationCenterPreferences />
        </div>

        {/* A signature is how one person signs off, so it sits with the mailbox
          rather than under tenant Settings. Full width because the list and
          preview need the room the two-up cards above do not. */}
        <EmailSignatures />

        <FollowUpSettings />

        <Card className={styles.Danger} noHover>
          <CardHeader>
            <div className={styles.Header}>
              <span className={styles.HeaderIcon} aria-hidden="true">
                <AlertTriangle />
              </span>
              <div className={styles.HeaderText}>
                <CardTitle>Delete account</CardTitle>
                <CardDescription>
                  Permanently delete your account and remove yourself from your teams. This cannot
                  be undone.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className={styles.DangerBody}>
            {eligibilityError && (
              <Alert type="error" className={styles.DangerAlert}>
                We could not check whether your account can be deleted.
                <div className={styles.Actions}>
                  <Button variant="outline" size="sm" onClick={() => void refetchEligibility()}>
                    Try again
                  </Button>
                </div>
              </Alert>
            )}
            {eligibility && !eligibility.canDelete && eligibility.reason && (
              <Alert type="warning" className={styles.DangerAlert}>
                {eligibility.reason}
              </Alert>
            )}
            <div className={styles.Actions}>
              <Button
                variant="danger"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={!eligibility?.canDelete || deleting}
              >
                Delete account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Modal isOpen={isEmailModalOpen} onClose={() => setIsEmailModalOpen(false)}>
        <ModalHeader>
          <ModalTitle>Change email</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {emailSentTo ? (
            <Alert type="success">
              We&apos;ve sent a verification link to <strong>{emailSentTo}</strong>. Open it to
              confirm the change. Your current email stays active until you do.
            </Alert>
          ) : (
            <>
              {emailError && (
                <Alert type="error" className={styles.ModalAlert}>
                  {emailError}
                </Alert>
              )}
              <FormGroup>
                <Label htmlFor="new-email">New email address</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                />
              </FormGroup>
              <p className={styles.ModalHint}>
                We&apos;ll send a verification link to the new address. The change takes effect only
                after you confirm it.
              </p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {emailSentTo ? (
            <Button variant="primary" onClick={() => setIsEmailModalOpen(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setIsEmailModalOpen(false)}
                disabled={emailSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleRequestEmailChange}
                disabled={emailSubmitting || !newEmail.trim()}
              >
                {emailSubmitting ? 'Sending…' : 'Send verification link'}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      {isDeleteModalOpen && (
        <ConfirmationModal onClose={() => setIsDeleteModalOpen(false)}>
          <ConfirmationModal.Body>
            <Heading level={3}>Delete your account?</Heading>
            <p>
              This permanently deletes your account and removes you from your teams. This action
              cannot be undone.
            </p>
          </ConfirmationModal.Body>
          <ConfirmationModal.Actions>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteAccount} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete account'}
            </Button>
          </ConfirmationModal.Actions>
        </ConfirmationModal>
      )}
    </InnerContainer>
  );
}
